import React, { useState, useEffect } from 'react';
import '../../styles/components/UsersList.scss'
import { getAppUsersAll, createAppUser, updateAppUser, deleteAppUserAll } from '../../api/appUsersApi.js';
import { getAppTemplatesAll } from '../../api/appTemplatesApi.js';
import { useLoader } from '../../context/LoaderContext.js';
import { Box, Grid, Card, CardContent, Typography, Button, Fab } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import UserModal from './UserModal.js';
import DeleteModal from '../DeleteModal.js';
import UsersDataGrid from './UserDataGrid.js';
import { useAuth } from '../../context/AuthContext.js';

function UsersList() {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const { showLoader, hideLoader } = useLoader();
  const { user } = useAuth();
  const loadUsers = () => {
    showLoader();
    getAppUsersAll()
      .then(response => setUsers(response.data))
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching users:', error));
  };

  const loadTemplates = () => {
    showLoader();
    getAppTemplatesAll()
      .then(response => setTemplates(response.data))
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching templates:', error));
  };

  const handleOpenDeleteModal = (user) => {
    setSelectedUser(user);
    setOpenDeleteModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenDeleteModal(false);
    setSelectedUser(null);
  };

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async (data, isNew) => {
    try {
      showLoader();
      let userMessage = isNew ? 'El usuario se creó correctamente' : 'El usuario se guardó correctamente';
      isNew ? await createAppUser(data) : await updateAppUser(selectedUser.id, data);

      setOpenModal(false);
      setSelectedUser(null);
      setOpenSnackbar({ open: true, text: userMessage, error: false });
      loadUsers();
    } catch (error) {
      if (error.type === 'duplicate') {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      } else {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      }
    } finally {
      hideLoader();
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      showLoader();
      await deleteAppUserAll(id);
      setOpenDeleteModal(false);
      setSelectedUser(null);
      setOpenSnackbar({ open: true, text: 'El usuario se eliminó correctamente', error: true });
      loadUsers();
    } catch (error) {
      setOpenSnackbar({ open: true, text: 'Hubo un error al eliminar el usuario', error: true });
    } finally {
      hideLoader();
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  useEffect(() => {
    loadUsers();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 234px)',
      pt: 3,
      pb: 1
    }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon'>
                <GroupIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Total
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {users.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon active-users'>
                <GroupIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Usuarios activos
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {Array.isArray(users) ? users?.filter(u => u.is_active).length : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {user?.isAdmin || true ? <div className="add-button-container">
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => handleOpenModal({})}>Crear usuario</Button>
      </div> : null}
      <Card className='user-list-datagrid-card' >
        <CardContent>
          <UsersDataGrid
            users={users}
            templates={templates}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleOpenModal={handleOpenModal}
          />
        </CardContent>
      </Card>

      <DeleteModal
        openConfirmModal={openDeleteModal}
        selectedItem={selectedUser}
        handleCloseConfirmModal={handleCloseConfirmModal}
        onDelete={handleDeleteUser}
        message={'Esta seguro de eliminar usuario: ' + selectedUser?.email}
      />

      <UserModal
        openModal={openModal}
        selectedUser={selectedUser}
        handleCloseModal={handleCloseModal}
        handleSaveUser={handleSaveUser}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UsersList;
